import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import BASE_URL from '../config/config';
import { Link, useNavigate } from 'react-router-dom';
import ContentLoader from 'react-content-loader';
import { FiBell, FiRefreshCw, FiCheck, FiCheckCircle, FiInbox, FiX, FiClock, FiAlertTriangle } from 'react-icons/fi';

// Store compartido a nivel de módulo: Header monta este componente dos veces
// (barra de escritorio + barra de burger en móvil). Sin esto cada instancia
// abría su propio polling de 5s en paralelo contra el backend.
let sharedState = {
  notificacionesTickets: [],
  notificacionesPersonalizadas: [],
  isLoading: true,
  error: null,
};
const listeners = new Set();
let intervalId = null;

const setSharedState = (patch) => {
  sharedState = { ...sharedState, ...patch };
  listeners.forEach((fn) => fn(sharedState));
};

const fetchNotificacionesShared = async (silent = false) => {
  if (!silent) setSharedState({ isLoading: true });
  try {
    const token = localStorage.getItem("AuthToken");
    if (!token) throw new Error("Token de autenticación no encontrado.");

    const decodedToken = jwtDecode(token);
    if (!decodedToken || !decodedToken.usuario || !decodedToken.role) {
      throw new Error("El token es inválido o carece de información requerida.");
    }

    const { usuario, role } = decodedToken;

    const responseTickets = await axios.get(`${BASE_URL}/ticket/notificaciones/${usuario}`, {
      params: { role },
    });

    const responseNotificaciones = await axios.get(`${BASE_URL}/notificaciones/${usuario}`, {
      headers: { Authorization: token }
    });

    setSharedState({
      notificacionesTickets: responseTickets.status === 200 ? (responseTickets.data.tickets || []) : sharedState.notificacionesTickets,
      notificacionesPersonalizadas: responseNotificaciones.status === 200 ? (responseNotificaciones.data.notificaciones || []) : sharedState.notificacionesPersonalizadas,
      isLoading: false,
      error: null,
    });
  } catch (err) {
    console.error('Error al obtener notificaciones:', err);
    setSharedState({ isLoading: false, error: 'Ocurrió un error al cargar las notificaciones.' });
  }
};

const Notificaciones = () => {
  const [state, setState] = useState(sharedState);
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const { notificacionesTickets, notificacionesPersonalizadas, isLoading, error } = state;

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  // Fecha corta y relativa para las tarjetas ("hace 5 min", "ayer, 18:04"),
  // con la fecha completa en el title por si hace falta el detalle.
  const formatearFecha = (valor) => {
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return '';

    const minutos = Math.round((Date.now() - fecha.getTime()) / 60000);
    if (minutos < 1) return 'ahora mismo';
    if (minutos < 60) return `hace ${minutos} min`;
    if (minutos < 24 * 60) return `hace ${Math.floor(minutos / 60)} h`;
    if (minutos < 48 * 60) {
      return `ayer, ${new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' }).format(fecha)}`;
    }
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    }).format(fecha);
  };

  const fechaCompleta = (valor) => {
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return '';
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(fecha);
  };

  const obtenerClasePrioridad = (prioridad) => {
    const clases = {
      'urgente': 'prioridad-urgente',
      'alta': 'prioridad-alta',
      'normal': 'prioridad-normal',
      'baja': 'prioridad-baja'
    };
    return clases[prioridad] || clases['normal'];
  };

  const obtenerTextoPrioridad = (prioridad) => {
    const textos = {
      'urgente': 'URGENTE',
      'alta': 'ALTA',
      'normal': 'NORMAL',
      'baja': 'BAJA'
    };
    return textos[prioridad] || 'NORMAL';
  };

  const marcarComoLeida = async (id) => {
    try {
      const token = localStorage.getItem("AuthToken");
      await axios.put(`${BASE_URL}/notificaciones/${id}/marcar-leida`, {}, {
        headers: { Authorization: token }
      });

      setSharedState({
        notificacionesPersonalizadas: sharedState.notificacionesPersonalizadas.filter(notif => notif.id !== id)
      });
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
    }
  };

  const manejarClickNotificacion = (notificacion) => {
    marcarComoLeida(notificacion.id);
    setIsDropdownOpen(false);

    if (notificacion.link) {
      if (notificacion.link.startsWith('/')) {
        navigate(notificacion.link);
      } else {
        window.open(notificacion.link, '_blank');
      }
    }
  };

  const marcarTodasComoLeidas = async () => {
    try {
      const token = localStorage.getItem("AuthToken");
      const decodedToken = jwtDecode(token);
      const { usuario } = decodedToken;

      await axios.put(`${BASE_URL}/notificaciones/${usuario}/marcar-todas-leidas`, {}, {
        headers: { Authorization: token }
      });

      setSharedState({ notificacionesPersonalizadas: [] });
    } catch (error) {
      console.error('Error al marcar todas como leídas:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  // Suscripción al store compartido: un único poller de 5s independientemente
  // de cuántas instancias de este componente estén montadas.
  useEffect(() => {
    listeners.add(setState);

    if (listeners.size === 1) {
      fetchNotificacionesShared();
      intervalId = setInterval(() => fetchNotificacionesShared(true), 5000);
    }

    return () => {
      listeners.delete(setState);
      if (listeners.size === 0 && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, []);

  const totalNotificaciones = notificacionesTickets.length + notificacionesPersonalizadas.length;

  // Cabecera compartida por los tres estados (cargando / error / lista) para
  // que el panel no cambie de altura ni de controles al refrescar.
  const renderCabecera = () => (
    <div className="cabecera-notificaciones">
      <div className="titulo-notificaciones">
        <h3>Notificaciones</h3>
        {totalNotificaciones > 0 && (
          <span className="contador-notificaciones">{totalNotificaciones}</span>
        )}
      </div>
      <div className="botones-cabecera">
        {notificacionesPersonalizadas.length > 0 && (
          <button
            className='boton-marcar-todas'
            onClick={marcarTodasComoLeidas}
            title="Marcar todas como leídas"
          >
            <FiCheckCircle /> <span>Marcar todo como leído</span>
          </button>
        )}
        <button
          className={`refresh-button ${isLoading ? 'cargando' : ''}`}
          onClick={() => fetchNotificacionesShared()}
          title="Refrescar"
          aria-label="Refrescar notificaciones"
        >
          <FiRefreshCw />
        </button>
        {/* Solo visible en el drawer móvil: en escritorio se cierra clicando fuera */}
        <button
          className="cerrar-notificaciones"
          onClick={() => setIsDropdownOpen(false)}
          title="Cerrar"
          aria-label="Cerrar notificaciones"
        >
          <FiX />
        </button>
      </div>
    </div>
  );

  return (
    <div className='modal-notificaciones' ref={dropdownRef}>
      <button
        className={`notificaciones ${isDropdownOpen ? 'active' : ''}`}
        onClick={toggleDropdown}
        aria-expanded={isDropdownOpen}
        aria-label={totalNotificaciones > 0 ? `Notificaciones (${totalNotificaciones} sin leer)` : 'Notificaciones'}
      >
        <FiBell />
        {
          totalNotificaciones > 0 && (
            <div className='num-notificacion'>{totalNotificaciones > 99 ? '99+' : totalNotificaciones}</div>
          )
        }
      </button>

      {/* Fondo oscuro del drawer móvil (oculto en escritorio por CSS) */}
      <div
        className={`fondo-notificaciones ${isDropdownOpen ? 'open' : ''}`}
        onClick={() => setIsDropdownOpen(false)}
      />

      <div className={`dropdown-notificaciones ${isDropdownOpen ? 'open' : ''}`} role="dialog" aria-label="Notificaciones">
        {isLoading ? (
          <div className="grid-notificaciones">
            {renderCabecera()}
            <div className="cuerpo-notificaciones">
              <ContentLoader height={220} width="100%" backgroundColor="#eef0f4" foregroundColor="#f8f9fb">
                <rect x="0" y="6" rx="10" ry="10" width="100%" height="62" />
                <rect x="0" y="78" rx="10" ry="10" width="100%" height="62" />
                <rect x="0" y="150" rx="10" ry="10" width="100%" height="62" />
              </ContentLoader>
            </div>
          </div>
        ) : error ? (
          <div className="grid-notificaciones">
            {renderCabecera()}
            <div className="cuerpo-notificaciones">
              <div className="error-notificaciones">
                <FiAlertTriangle />
                <p>{error}</p>
                <button className="boton-reintentar" onClick={() => fetchNotificacionesShared()}>
                  Reintentar
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className='grid-notificaciones'>
            {renderCabecera()}

            <div className="cuerpo-notificaciones">
              {/* Notificaciones personalizadas */}
              {notificacionesPersonalizadas.length > 0 && (
                <div className="seccion-notificaciones">
                  {notificacionesTickets.length > 0 && (
                    <p className="subtitulo-notificaciones">Avisos</p>
                  )}
                  {notificacionesPersonalizadas.map((notif) => (
                    <div
                      key={notif.id}
                      className={`notificacion notificacion-personalizada ${notif.link ? 'clicable' : ''}`}
                      onClick={() => manejarClickNotificacion(notif)}
                    >
                      <div className="contenido-notificacion">
                        <div className="header-notificacion-contenido">
                          <p className='not-titulo'>{notif.titulo}</p>
                          {/* Solo se etiquetan las prioridades que piden atención:
                              "normal" y "baja" no aportan y ensucian la lista. */}
                          {(notif.prioridad === 'urgente' || notif.prioridad === 'alta') && (
                            <span className={`badge-prioridad ${obtenerClasePrioridad(notif.prioridad)}`}>
                              {obtenerTextoPrioridad(notif.prioridad)}
                            </span>
                          )}
                        </div>
                        <p className='not-mensaje'>{notif.mensaje}</p>
                        <p className='not-fecha' title={fechaCompleta(notif.createdAt)}>
                          <FiClock /> {formatearFecha(notif.createdAt)}
                        </p>
                      </div>
                      <button
                        className="boton-marcar-leida"
                        onClick={(e) => {
                          e.stopPropagation();
                          marcarComoLeida(notif.id);
                        }}
                        title="Marcar como leída"
                        aria-label="Marcar como leída"
                      >
                        <FiCheck />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Notificaciones de tickets */}
              {notificacionesTickets.length > 0 && (
                <div className="seccion-notificaciones">
                  {notificacionesPersonalizadas.length > 0 && (
                    <p className="subtitulo-notificaciones">Tickets</p>
                  )}
                  {notificacionesTickets.map((ticket, index) => (
                    <Link
                      to={`/ticket/${ticket.identificador}`}
                      className='notificacion notificacion-ticket clicable'
                      key={index}
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <div className="contenido-notificacion">
                        <p className='not-id'><b>#{ticket.identificador}</b> · {ticket.asunto}</p>
                        <p className='not-mensaje'>{ticket.nombre} respondió al ticket.</p>
                        <p className='not-fecha' title={fechaCompleta(ticket.updatedAt)}>
                          <FiClock /> {formatearFecha(ticket.updatedAt)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Sin notificaciones */}
              {totalNotificaciones === 0 && (
                <div className='no-pendiente'>
                  <FiInbox />
                  <p>Todo al día</p>
                  <span>No tienes notificaciones nuevas.</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notificaciones;
